import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MetaChip } from "../../../components/UIToolkit/Badges/MetaChip";
import { classifyEmployeeMatch } from "../useAttemptsFilter";
import { EMPLOYEE_MATCH_CELL_LABEL_KEYS, EMPLOYEE_MATCH_DISPLAY } from "./employeeMatchDisplay";

type Props = {
	/** The raw "Employee Match" cell as the transform emitted it, e.g. `matched (90)` / `merged (60.9)` / `ambiguous`. */
	value: string;
	dataTestId: string;
};

/** The score the transform prints after the verdict (`merged (60.9)` → `60.9`); absent on `ambiguous` / `not found`. */
function matchScore(value: string): string | null {
	const parenthesized = value.match(/\(([^)]*)\)/);
	return parenthesized === null ? null : parenthesized[1];
}

/**
 * One "Employee Match" cell of the transform preview. A verdict that needs a second look (unmatched,
 * merged, ambiguous) is a tokenized `MetaChip` carrying a "what to do" tooltip, so a column of them
 * reads as a scannable line of flags; a plain `matched` — the overwhelming majority in a healthy file —
 * stays default-colored text so the dense CSV grid isn't drowned in chips.
 *
 * The verdict word is translated while the score is printed exactly as received (it is not a
 * percentage), so the cell reads in the user's language without inventing precision the matcher
 * did not report. A cell this cannot classify — an empty one, or a CSV from before the column
 * existed — is shown verbatim rather than guessed at.
 */
export function EmployeeMatchCell({ value, dataTestId }: Props) {
	const { t } = useTranslation();
	const state = classifyEmployeeMatch(value);

	if (state !== null) {
		const score = matchScore(value);
		const verdict = t(EMPLOYEE_MATCH_CELL_LABEL_KEYS[state]);
		const label = score === null ? verdict : `${verdict} (${score})`;

		if (state !== "matched") {
			const { tone, tooltipKey } = EMPLOYEE_MATCH_DISPLAY[state];
			return <MetaChip label={label} tone={tone} tooltip={t(tooltipKey)} dataTestId={dataTestId} sx={{ minWidth: 0 }} />;
		}
		return <PlainCell text={label} dataTestId={dataTestId} />;
	}

	return <PlainCell text={value} dataTestId={dataTestId} />;
}

function PlainCell({ text, dataTestId }: { text: string; dataTestId: string }) {
	return (
		<Typography
			variant="body2"
			data-testid={dataTestId}
			// `minWidth: 0` lets the cell's flex child shrink, so a long value ellipsizes instead of being hard-clipped.
			sx={{ color: "text.primary", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
		>
			{text}
		</Typography>
	);
}
